<template>

    <b-overlay :show="overlayShow" rounded="sm" class="h-100">

        <Card :no-padding="noPadding" class="m-0 shadow-none" :header-id="headerId">

            <template #nav>
                <slot name="headerSide"></slot>
            </template>

            <template #header>
                <slot name="header" :items="internalItems"></slot>
                <div v-if="header && !$scopedSlots.header && !$slots.header" class="d-flex gap-2 align-items-center">
                    <span>{{ header }}</span>
                    <JsonToCsv
                        v-if="showExportButton"
                        icon="download"
                        variant="light"
                        :icon-size="14"
                        class="p-1 rounded-max mb-auto width-25 height-25"
                        :title="$t('export')"
                        :items="exportItems"
                        :fields="exportFields"
                        :labels="exportLabels"
                        :name="exportFilename"
                    />
                </div>
            </template>

            <ErrorMessage :value="error || tableError" class="table-error" @dismissed="$emit('update:error', null)" />

            <div :class="{'overflow-auto': overflow}">

                <slot name="tableHeader" :items="internalItems"></slot>

                <b-table
                    ref="bTable"
                    class="m-0"
                    :stacked="stacked"
                    :class="tableClass"
                    :hover="hover"
                    :items="tableItems"
                    :fields="tableFields"
                    :empty-text="$t('noResult')"
                    :empty-filtered-text="$t('noResult')"
                    show-empty
                    no-sort-reset
                    :busy="tableBusy"
                    :current-page="tableTotalRows === internalItems.length ? tableCurrentPage : 1"
                    :per-page.sync="tablePerPage"
                    :sort-by.sync="tableSortBy"
                    :sort-desc.sync="tableSortDesc"
                    :sort-direction="sortDirection.toLowerCase()"
                    sort-icon-left
                    :foot-clone="footClone || showTotal"
                    no-footer-sorting
                    v-bind="tableAttrs"
                    v-on="tableListeners"
                    @input="onInput"
                    @update:busy="onUpdateBusy"
                    @filtered="onFiltered"
                >

                    <template #head($selected)>
                        <div class="position-relative w-100 h-100 cursor-pointer table-selectable-all-item" :class="{'cursor-not-allowed': isAnyItemBusy || !selectableItems.length}" @click="toggleAllRow()">
                            <div class="position-absolute w-100 h-100 z-index-10"></div>
                            <b-form-checkbox
                                class="z-index-1 table-selectable-all-checkbox"
                                :checked="isAllRowsSelected"
                                :indeterminate="!isAllRowsSelected && isAnyRowsSelected"
                                :disabled="isAnyItemBusy || !selectableItems.length"
                                readonly
                                @click.native.prevent
                            />
                        </div>
                    </template>

                    <template #cell($selected)="data">
                        <div v-if="isRowSelectable(data)" class="position-relative w-100 h-100 cursor-pointer table-selectable-item" :class="{'cursor-not-allowed': isRowBusy(data)}" @click="toggleRow(data)">
                            <div class="position-absolute w-100 h-100 z-index-10"></div>
                            <b-form-checkbox class="z-index-1 table-selectable-checkbox" :checked="isRowSelected(data)" readonly :disabled="isRowBusy(data)" @click.native.prevent />
                        </div>
                        <!-- <b-spinner v-if="isItemBusy(data.item.id)" small /> -->
                    </template>

                    <template v-if="showTotal" #foot()>
                        <div></div>
                    </template>
                    <template v-for="item in tableFields" #[`foot(${item.key})`]>
                        <div v-if="showTotal" :key="item.key" class="text-nowrap">
                            <template v-if="item.total === true">
                                {{ getItemTotalValue(item) }}
                            </template>
                        </div>
                    </template>

                    <template v-if="!internalBusy" #table-busy>
                        <div class="d-flex justify-content-center my-2">
                            <b-spinner />
                        </div>
                    </template>

                    <template v-for="slotName in Object.keys($slots)" #[slotName]>
                        <slot :name="slotName"></slot>
                    </template>

                    <template v-for="slotName in Object.keys($scopedSlots)" #[slotName]="slotProps">
                        <slot :name="slotName" v-bind="slotProps"></slot>
                    </template>

                </b-table>

            </div>

            <div v-if="pagination" class="table-footer d-flex gap-3 align-items-center mt-3">

                <b-dropdown variant="primary" class="table-per-page" :disabled="loading || tableBusy">
                    <template #button-content>
                        <span>{{ tablePerPageLabel }}</span>
                        <i class="icon ml-1">
                            <feather type="chevron-down" class="align-middle" />
                        </i>
                    </template>
                    <b-dropdown-item v-for="item in perPageItems" :key="item.value" :class="`item-${item.value}`" :active="item.value === tablePerPage" @click="() => tablePerPage = item.value">
                        {{ item.label }}
                    </b-dropdown-item>
                </b-dropdown>

                <slot name="innerPagination"></slot>

                <Pagination
                    v-if="tablePerPage !== null"
                    :value="tableCurrentPage"
                    :total-rows="tableTotalRows"
                    :per-page="tablePerPage"
                    :disabled="loading || tableBusy"
                    class="ml-auto"
                    @input="val => (loading ? null : (tableCurrentPage = val))"
                />

            </div>

            <slot name="afterTable"></slot>

        </Card>

    </b-overlay>

</template>



<script>
    import slugify from 'slugify';
    import sumBy from 'lodash/sumBy';
    import storageKeys from '@/Storage';
    import Pagination from '@/components/common/Pagination.vue';
    import JsonToCsv from '@/components/common/JsonToCsv.vue';

    const availablePerPageValues = [10, 20, 25, 50, 100, 500, 1000, null];

    export default {
        name: 'DataTable',
        inheritAttrs: false,
        components: {
            Pagination,
            JsonToCsv,
        },
        props: {
            value: {
                type: Array,
                default: () => [],
            },
            items: {
                type: [Array, Function],
                required: true,
            },
            fields: {
                type: Array,
                default: () => [],
            },
            pagination: {
                type: Boolean,
                default: true,
            },
            totalRows: {
                type: Number,
                default: null,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            perPageValues: {
                type: Array,
                default: () => [10, 25, 50, 100],
                validator: value => value.every(item => availablePerPageValues.includes(item)),
            },
            perPage: {
                type: Number,
                default: 10,
                validator: value => availablePerPageValues.includes(value),
            },
            currentPage: {
                type: Number,
                default: 1,
            },
            sortBy: {
                type: String,
                default: 'id',
            },
            sortDirection: {
                type: String,
                default: 'DESC',
            },
            noPadding: {
                type: Boolean,
                default: false,
            },
            error: {
                type: [Error, Object],
                default: null,
            },
            header: {
                type: String,
                default: '',
            },
            selectable: {
                type: Boolean,
                default: false,
            },
            selectableIdKey: {
                type: String,
                default: '',
            },
            stacked: {
                type: String,
            },
            selectableItemKey: {
                type: String,
                default: null,
            },
            selectableItemFunction: {
                type: Function,
                default: null,
            },
            busyItems: {
                type: Array,
                default: () => [],
            },
            hover: {
                type: Boolean,
                default: true,
            },
            headerId: {
                type: String,
                default: null,
            },
            tableClass: {
                type: [String, Object, Array],
                default: null,
            },
            overflow: {
                type: Boolean,
                default: true,
            },
            showExportButton: {
                type: Boolean,
                default: false,
            },
            footClone: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            const isProvider = typeof this.items === 'function';
            return {
                filteredItems: null,
                tablePerPage: this.pagination ? this.perPage : null,
                tableCurrentPage: this.currentPage,
                tableSortBy: this.sortBy,
                tableSortDesc: this.sortDirection.toLowerCase() === 'desc',
                selectedRows: [],
                tableError: null,
                internalItems: isProvider ? [] : this.items,
                internalBusy: false,
            };
        },
        computed: {
            perPageItems() {
                return this.perPageValues.map(item => ({ value: item, label: this.$tx(`perPageItem.${item}`, item) }));
            },
            tableItems() {
                if (this.isProvider) {
                    return (...args) => {
                        this.tableError = null;
                        this.selectedRows = [];
                        return this.items(...args).catch(error => {
                            this.internalBusy = false;
                            this.tableError = error;
                            return [];
                        });
                    };
                }
                return this.items;
            },
            isProvider() {
                return typeof this.items === 'function';
            },
            overlayShow() {
                return !!(this.loading && this.internalItems.length) || this.internalBusy;
            },
            tableBusy() {
                return !!(this.loading && !this.internalItems.length);
            },
            tableAttrs() {
                return this.$attrs;
            },
            tableListeners() {
                const { input, change, ...listeners } = this.$listeners;
                return listeners;
            },
            tableFields() {
                const fields = this.fields.filter(item => item.show !== false);
                if (this.isSelectable === true) return [{ key: '$selected', label: '', tdClass: 'width-50' }, ...fields];
                return fields;
            },
            tableTotalRows() {
                return this.totalRows ?? this.filteredItems?.length ?? this.internalItems.length;
            },
            tablePerPageLabel() {
                if (!this.pagination) return '';
                return this.perPageItems.find(item => item.value === this.tablePerPage).label;
            },
            isSelectable() {
                return !!this.selectable;
            },
            isAllRowsSelected() {
                const allSelectableItemsIds = this.getAllSelectableItemsIds();
                return allSelectableItemsIds.length && allSelectableItemsIds.every(id => this.selectedRows.includes(id));
            },
            isAnyRowsSelected() {
                const allSelectableItemsIds = this.getAllSelectableItemsIds();
                return allSelectableItemsIds.some(id => this.selectedRows.includes(id));
            },
            isAnyItemBusy() {
                return this.busyItems.length > 0;
            },
            selectableItems() {
                return this.internalItems.filter(this.isItemSelectable);
            },
            exportFilename() {
                const locale = window.localStorage.getItem(storageKeys.LOCALE) || 'en';
                const name = slugify(this.header, { replacement: '-', lower: true, locale }) || 'data';
                return `${name}.csv`;
            },
            exportFields() {
                return this.fields.filter(item => item.exportable === true).map(item => item.key);
            },
            exportLabels() {
                return Object.fromEntries(this.fields.map(item => [item.key, item.exportLabel || item.label]));
            },
            exportFormatters() {
                return this.fields.reduce((acc, item) => {
                    if (typeof item.exportFormatter === 'function') {
                        acc[item.key] = item.exportFormatter; // eslint-disable-line no-param-reassign
                    } else if (typeof item.formatter === 'function') {
                        acc[item.key] = item.formatter; // eslint-disable-line no-param-reassign
                    }
                    return acc;
                }, {});
            },
            showTotal() {
                return this.tableFields.some(item => item.total === true);
            },
        },
        watch: {
            perPage() {
                this.tablePerPage = this.pagination ? this.perPage : null;
            },
            currentPage() {
                this.tableCurrentPage = this.currentPage;
            },
            sortDirection() {
                this.tableSortDesc = this.sortDirection.toLowerCase() === 'desc';
            },
            sortBy() {
                this.tableSortBy = this.sortBy;
            },
            isSelectable: {
                handler() {
                    if (this.isSelectable === true && !this.selectableIdKey) {
                        this.tableError = new Error('Prop "selectableIdKey" is required!');
                    } else {
                        this.tableError = null;
                    }
                },
                immediate: true,
            },
            value: {
                handler() {
                    if (!this.isSameArrays(this.value, this.selectedRows)) this.selectedRows = this.value;
                },
                immediate: true,
            },
            selectedRows(newValue, oldValue) {
                if (!this.isSameArrays(newValue, oldValue)) this.$emit('input', newValue);
            },
            internalItems(newValue, oldValue) {
                if (!this.isProvider && !this.isSameArrays(newValue, oldValue)) this.selectedRows = [];
            },
            fields() {
                this.removeNonexistentSelectedRows();
            },
            tablePerPage(val) {
                this.$emit('update:per-page', val);
            },
            tableCurrentPage(val) {
                this.$emit('update:current-page', val);
            },
            tableSortBy(val) {
                this.$emit('update:sort-by', val);
            },
            tableSortDesc(val) {
                this.$emit('update:sort-direction', val ? 'DESC' : 'ASC');
            },
            selectableItems(newValue, oldValue) {
                if (!this.isSameArrays(newValue, oldValue)) {
                    const allSelectableItemsIds = this.getAllSelectableItemsIds();
                    this.selectedRows = this.selectedRows.filter(id => allSelectableItemsIds.includes(id));
                }
            },
        },
        methods: {
            sumBy,
            onFiltered(filteredItems) {
                this.filteredItems = filteredItems;
                this.tableCurrentPage = 1;
            },
            onUpdateBusy(busy) {
                this.internalBusy = this.isProvider ? busy : false;
            },
            onInput(e) {
                this.internalItems = (this.isProvider ? this.$refs.bTable?.computedItems : this.items) || [];
            },
            isSameArrays(arrayOne, arrayTwo) {
                if (arrayOne.length !== arrayTwo.length) return false;
                const sortedNew = [...arrayOne].sort((a, b) => a > b);
                const sortedOld = [...arrayTwo].sort((a, b) => a > b);
                try {
                    return JSON.stringify(sortedNew) === JSON.stringify(sortedOld);
                } catch (error) {
                    return null;
                }
            },
            toggleRow(row) {
                if (this.isRowBusy(row)) return;
                if (this.isRowSelected(row)) {
                    this.unselectRow(row);
                } else {
                    this.selectRow(row);
                }
            },
            selectRow(row) {
                if (this.isRowSelectable(row)) {
                    const rowId = row.item[this.selectableIdKey];
                    this.selectedRows = [...this.selectedRows, rowId];
                }
            },
            unselectRow(row) {
                const rowId = row.item[this.selectableIdKey];
                this.selectedRows = this.selectedRows.filter(id => id !== rowId);
            },
            isRowSelectable(row) {
                return this.isItemSelectable(row.item);
            },
            isItemSelectable(item) {
                if (typeof this.selectableItemFunction === 'function') return this.selectableItemFunction(item);
                if (this.selectableItemKey) return !!item[this.selectableItemKey];
                return item.selectable !== false;
            },
            isRowSelected(row) {
                const rowId = row.item[this.selectableIdKey];
                return this.selectedRows.includes(rowId);
            },
            selectAllRows() {
                this.selectedRows = this.getAllSelectableItemsIds();
            },
            unselectAllRows() {
                this.selectedRows = [];
            },
            getAllSelectableItemsIds() {
                return this.selectableItems.map(item => item[this.selectableIdKey]);
            },
            removeNonexistentSelectedRows() {
                const allSelectableItemsIds = this.getAllSelectableItemsIds();
                const someSelectedRowDoesNotExist = this.selectedRows.some(id => !allSelectableItemsIds.includes(id));
                if (someSelectedRowDoesNotExist) this.selectedRows = this.selectedRows.filter(id => allSelectableItemsIds.includes(id));
            },
            toggleAllRow() {
                if (this.isAnyItemBusy) return;
                if (this.isAllRowsSelected) {
                    this.unselectAllRows();
                } else {
                    this.selectAllRows();
                }
            },
            isRowBusy(row) {
                const rowId = row.item[this.selectableIdKey];
                return this.isItemBusy(rowId);
            },
            isItemBusy(id) {
                return this.busyItems.includes(id);
            },
            getItemTotalValue(item) {
                const tableItems = this.filteredItems || this.tableItems;
                const valueSumFn = () => (typeof item.totalValue === 'function' ? item.totalValue(item, tableItems) : sumBy(tableItems, item.key));
                return this.getItemTotalFormatter(item)(valueSumFn());
            },
            getItemTotalFormatter(item) {
                if (typeof item.totalFormatter === 'function') return item.totalFormatter;
                if (typeof item.formatter === 'function') return item.formatter;
                return value => value;
            },
            exportItems() {
                const items = this.isProvider ? this.internalItems : this.items;
                const formattersKeys = Object.keys(this.exportFormatters);
                if (formattersKeys.length) {
                    return items.map(item => {
                        const keysFormatted = formattersKeys.reduce((acc, key) => {
                            acc[key] = this.exportFormatters[key](item[key]); // eslint-disable-line no-param-reassign
                            return acc;
                        }, {});
                        return { ...item, ...keysFormatted };
                    });
                }
                return items;
            },
        },
    };
</script>


<i18n lang="json" locale="pl" scope="local">
{
    "perPageItem": {
        "null": "Wszystkie"
    }
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "perPageItem": {
        "null": "All"
    }
}
</i18n>

